/**
 * Phase J.2 Pass 2 — InvoicePayModal
 *
 * Customer-facing Stripe Elements ACH pay flow.
 *
 * Contract:
 *   <InvoicePayModal
 *     invoice={invoice}              // customer-shaped invoice dict
 *     open={open}
 *     onOpenChange={setOpen}
 *     onInitiated={() => …}          // called right before Stripe redirects
 *   />
 *
 * Flow:
 *   1. On open, lazy-load @stripe/stripe-js, POST
 *      /customer/invoices/{id}/payment-intents to fetch {client_secret,
 *      publishable_key}, then loadStripe(publishable_key).
 *   2. Render <Elements clientSecret> wrapping a PaymentElement locked to
 *      us_bank_account via paymentMethodOrder + wallets disabled.
 *   3. "Authorize" → stripe.confirmPayment({ elements, confirmParams:
 *      { return_url: …?pay_return=1 } }). Stripe redirects after the
 *      Financial Connections flow finishes.
 *
 * Stripe never receives our session token; we never expose the secret key
 * or webhook secret. publishable_key comes from the server response (per
 * acceptance criterion 10) so we never bake env-specific publishable
 * keys into the React bundle.
 */
import React, { useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
    Elements,
    PaymentElement,
    useElements,
    useStripe,
} from "@stripe/react-stripe-js";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle, Building2, Lock, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

function fmtMoney(cents) {
    if (cents == null) return "—";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Inner form: needs to live INSIDE <Elements> to use Stripe hooks. */
function PayInner({ invoice, onInitiated, closeModal }) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [elementReady, setElementReady] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const returnUrl = `${window.location.origin}/customer/invoices/${invoice.id}?pay_return=1`;
            // Tell the parent we're handing control to Stripe so the
            // modal can close cleanly before redirect. If Stripe returns
            // an inline error (no redirect happened), we re-open the
            // error state below.
            onInitiated && onInitiated();
            const result = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: returnUrl },
            });
            // We only get here when Stripe returns an error (no redirect).
            if (result?.error) {
                setError(
                    result.error.message ||
                        "We couldn't submit your bank authorization. Please try again."
                );
            }
        } catch (err) {
            setError(err?.message || "Unexpected error submitting payment.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="invoice-pay-form">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
                <PaymentElement
                    options={{
                        layout: "tabs",
                        // Defense-in-depth — server PI already restricts to
                        // us_bank_account, but order/wallets keep the UI
                        // strictly ACH.
                        paymentMethodOrder: ["us_bank_account"],
                        wallets: { applePay: "never", googlePay: "never" },
                        fields: { billingDetails: { address: "never" } },
                    }}
                    onReady={() => setElementReady(true)}
                    onLoadError={(ev) =>
                        setError(
                            ev?.error?.message ||
                                "Stripe Elements failed to load. Refresh and try again."
                        )
                    }
                />
            </div>

            <div className="rounded-md bg-slate-50 ring-1 ring-slate-200 p-3 text-xs text-slate-600 leading-relaxed">
                <p className="flex items-start gap-2">
                    <ShieldCheck size={14} className="mt-0.5 text-emerald-700 shrink-0" />
                    <span>
                        By authorizing, you allow RentFlow and its payments
                        partner Stripe to debit the bank account selected for{" "}
                        <strong>{fmtMoney(invoice.total_cents)}</strong> for
                        invoice <strong>{invoice.invoice_number}</strong>. ACH
                        debits typically settle in 3–5 business days. You may
                        revoke this authorization by contacting us before the
                        debit posts.
                    </span>
                </p>
            </div>

            {error && (
                <div
                    className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2"
                    data-testid="invoice-pay-error"
                >
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            <DialogFooter className="gap-2 pt-1">
                <Button
                    type="button"
                    variant="ghost"
                    onClick={closeModal}
                    disabled={submitting}
                    data-testid="invoice-pay-cancel"
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    disabled={!stripe || !elements || submitting || !elementReady}
                    className="bg-blue-900 hover:bg-blue-800 gap-2 min-w-[160px]"
                    data-testid="invoice-pay-authorize"
                >
                    {submitting ? (
                        <>
                            <Loader2 size={14} className="animate-spin" /> Authorizing…
                        </>
                    ) : (
                        <>
                            <Lock size={14} /> Authorize {fmtMoney(invoice.total_cents)}
                        </>
                    )}
                </Button>
            </DialogFooter>
        </form>
    );
}

export default function InvoicePayModal({
    invoice,
    open,
    onOpenChange,
    onInitiated,
}) {
    const [boot, setBoot] = useState({ loading: false, error: null, clientSecret: null, stripePromise: null });
    // We only want to mint a PaymentIntent once per modal opening; track it
    // with a ref so re-renders don't re-trigger the API call.
    const fetchedFor = useRef(null);

    useEffect(() => {
        if (!open || !invoice?.id) return;
        if (fetchedFor.current === invoice.id) return;
        fetchedFor.current = invoice.id;
        setBoot({ loading: true, error: null, clientSecret: null, stripePromise: null });
        (async () => {
            try {
                const { data } = await api.post(
                    `/customer/invoices/${invoice.id}/payment-intents`
                );
                if (!data?.client_secret || !data?.publishable_key) {
                    throw new Error("Stripe payment session was not configured. Please contact support.");
                }
                const stripePromise = loadStripe(data.publishable_key);
                setBoot({
                    loading: false,
                    error: null,
                    clientSecret: data.client_secret,
                    stripePromise,
                });
            } catch (err) {
                setBoot({
                    loading: false,
                    error: formatApiError(err),
                    clientSecret: null,
                    stripePromise: null,
                });
            }
        })();
    }, [open, invoice?.id]);

    // Reset state whenever the modal closes so a re-open is a clean slate.
    useEffect(() => {
        if (!open) {
            fetchedFor.current = null;
            setBoot({ loading: false, error: null, clientSecret: null, stripePromise: null });
        }
    }, [open]);

    const close = () => onOpenChange && onOpenChange(false);

    if (!invoice) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[520px]"
                data-testid="invoice-pay-modal"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-xl text-slate-900 flex items-center gap-2">
                        <Building2 size={18} className="text-blue-900" />
                        Pay {fmtMoney(invoice.total_cents)} with bank account
                    </DialogTitle>
                    <DialogDescription className="text-slate-500">
                        Invoice <span className="font-mono text-slate-700">{invoice.invoice_number}</span> ·
                        ACH bank transfer. Settlement typically takes 3–5 business days.
                    </DialogDescription>
                </DialogHeader>

                {boot.loading && (
                    <div
                        className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"
                        data-testid="invoice-pay-loading"
                    >
                        <Loader2 size={14} className="animate-spin" />
                        Preparing secure bank-connect flow…
                    </div>
                )}

                {boot.error && (
                    <div
                        className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2"
                        data-testid="invoice-pay-boot-error"
                    >
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <span>{boot.error}</span>
                    </div>
                )}

                {boot.clientSecret && boot.stripePromise && (
                    <Elements
                        stripe={boot.stripePromise}
                        options={{
                            clientSecret: boot.clientSecret,
                            appearance: {
                                theme: "stripe",
                                variables: {
                                    colorPrimary: "#1e3a8a",
                                    colorText: "#0f172a",
                                    fontFamily:
                                        'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
                                    borderRadius: "8px",
                                },
                            },
                        }}
                    >
                        <PayInner
                            invoice={invoice}
                            onInitiated={onInitiated}
                            closeModal={close}
                        />
                    </Elements>
                )}
            </DialogContent>
        </Dialog>
    );
}
